import React, { useEffect, useState } from 'react';
import { Lock, PauseCircle, ArrowRight, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * ReadOnlyBanner — Sprint 198
 * ───────────────────────────
 * Renders a contextual banner at the top of the dashboard when the user's
 * account is paused (either 90-day cooling-off or indefinite "memory lane"
 * archive mode). One-tap reactivate from the banner itself.
 *
 * Mounting: drop into any dashboard layout — it self-fetches the user's
 * account mode and renders nothing if the account is fully active.
 */

const PURPLE = '#7B4FD8';
const ORANGE = '#F5831F';

const ReadOnlyBanner = () => {
  const [mode, setMode] = useState(null);
  const [pauseMode, setPauseMode] = useState(null);
  const [reactivating, setReactivating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient.request('/api/account/mode')
      .then((res) => {
        if (cancelled) return;
        setMode(res.account_mode);
        setPauseMode(res.pause_mode);
      })
      .catch(() => { /* free tier user, no subscription — no banner */ });
    return () => { cancelled = true; };
  }, []);

  const reactivate = async () => {
    setReactivating(true);
    try {
      await apiClient.request('/api/account/reactivate', { method: 'POST' });
      setMode('active');
      // Soft refresh so cards reload in their active state.
      window.location.reload();
    } catch {
      setReactivating(false);
    }
  };

  if (!mode || mode === 'active') return null;

  const isIndefinite = mode === 'paused_indefinite' || pauseMode === 'indefinite';

  const copy = isIndefinite
    ? {
      title: 'Memory-lane mode',
      subtitle: 'Your account is safely archived. Trophies, conversations, journey — all preserved. Welcome back anytime.',
      cta: 'Wake my account up',
      icon: <Lock size={18} />,
      bg: `linear-gradient(135deg, ${PURPLE}E6 0%, #5B2BB8E6 100%)`,
    }
    : {
      title: 'Account paused (90-day cooling-off)',
      subtitle: 'Read-only access while you take a break. Auto-resumes — or reactivate any time.',
      cta: 'Reactivate now',
      icon: <PauseCircle size={18} />,
      bg: `linear-gradient(135deg, ${ORANGE}E6 0%, #FFA45AE6 100%)`,
    };

  return (
    <div
      className="rounded-2xl p-4 sm:p-5 mb-5 text-white relative overflow-hidden"
      style={{ background: copy.bg, boxShadow: '0 6px 20px rgba(123,79,216,0.25)' }}
      data-testid="read-only-banner"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5">
        <div className="rounded-full p-2.5 flex-shrink-0" style={{ background: 'rgba(255,255,255,0.18)' }}>
          {copy.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-bold mb-0.5" style={{ opacity: 0.85 }}>{copy.title}</p>
          <p className="text-sm sm:text-base font-medium text-white/95">{copy.subtitle}</p>
        </div>
        <button
          onClick={reactivate}
          disabled={reactivating}
          className="w-full sm:w-auto px-5 py-2.5 rounded-full font-bold text-sm bg-white disabled:opacity-60 transition-transform hover:scale-[1.02] active:scale-[0.98] inline-flex items-center justify-center gap-2"
          style={{ color: isIndefinite ? PURPLE : ORANGE }}
          data-testid="reactivate-account-btn"
        >
          {reactivating ? <Loader2 size={14} className="animate-spin" /> : <>{copy.cta} <ArrowRight size={14} /></>}
        </button>
      </div>
    </div>
  );
};

export default ReadOnlyBanner;
